/*
  Cópia de `mpf-slots-frontend/components/theme/SlotRenderer/SlotRenderer.jsx`
  com uma regra a mais (AB#11373).

  A lateral de uma página recebe, mesclados, os blocos definidos nela e os
  herdados de todas as páginas ancestrais (`Slots.get_data` no backend). Como o
  menu de contexto está em várias páginas da mesma árvore (as raízes recebem um
  no provisionamento e os editores acrescentam outros abaixo), a página mais
  funda chegava a desenhar o mesmo menu duas ou três vezes, um por nível: duas
  navegações laterais visíveis e dois landmarks "Submenu" para o leitor de tela
  (medido em homologação na auditoria do Design System e no LAB, onde a página
  do Conselho Superior mostrava três).

  Aqui, para os tipos de bloco que só fazem sentido uma vez por lateral, fica
  só um: o definido na própria página, se houver; senão, o herdado do
  ancestral mais próximo (`_v_inherit_level`, marcado pelo backend em
  `ministerio.publico.federal.slots`). A ordem do leiaute só desempata: uma
  página que já salvou a lateral guarda os ids herdados na ordem daquele
  momento, e o menu criado depois em Unidades ficava atrás do da raiz. Os
  demais tipos passam como sempre. A lista de tipos vem de
  `config.settings.mpfSlotsBlocosUnicos`, com um padrão abaixo.
*/
import React from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import Registry from '@plone/volto/registry';
import RenderSlotFill from 'mpf-slots-frontend/components/theme/SlotRenderer/RenderSlotFill';

export const TIPOS_UNICOS_PADRAO = [
  'contextNavigation',
  'mpfUnidadeResponsavel',
];

/**
 * Distância do bloco até a página que o define: 0 para o próprio, 1 para o
 * pai e assim por diante. Herdado sem a marca vai para o fim.
 */
export function distanciaDaOrigem(bloco) {
  if (!bloco?._v_inherit) return 0;
  return bloco._v_inherit_level ?? Infinity;
}

/**
 * Devolve o leiaute sem repetição dos tipos únicos: para cada tipo da lista,
 * fica o bloco próprio da página (sem `_v_inherit`) ou, na falta dele, o
 * herdado do ancestral mais próximo; empate fica com o primeiro do leiaute.
 * Blocos de outros tipos não são tocados.
 */
export function semRepeticao(layout, components, tipos = TIPOS_UNICOS_PADRAO) {
  const escolhido = {};
  for (const id of layout) {
    const bloco = components[id];
    const tipo = bloco?.['@type'];
    if (!tipos.includes(tipo)) continue;
    const atual = escolhido[tipo];
    if (
      !atual ||
      distanciaDaOrigem(bloco) < distanciaDaOrigem(components[atual])
    ) {
      escolhido[tipo] = id;
    }
  }
  return layout.filter((id) => {
    const tipo = components[id]?.['@type'];
    return !tipos.includes(tipo) || escolhido[tipo] === id;
  });
}

/**
 * O bloco `id` some da lateral por causa de `semRepeticao`? Usado no editor de
 * slots (`config.settings.slotsBlockNotShown`) para mostrar compacto o que
 * não vale nesta página. Blocos ocultos ficam de fora da disputa, como na
 * página.
 */
export function blocoNaoExibido(id, formData, tipos = TIPOS_UNICOS_PADRAO) {
  const components = formData?.blocks || {};
  if (!tipos.includes(components[id]?.['@type'])) return false;
  const layout = (formData?.blocks_layout?.items || []).filter(
    (blocoId) => !components[blocoId]?.['v:hidden'],
  );
  return !semRepeticao(layout, components, tipos).includes(id);
}

const DefaultSlotWrapper = ({ name, children }) => (
  <div id={`slot-${name}`}>{children}</div>
);

const defaultComputeLayout = ({ staticFills, persistentFills }) => [
  ...(persistentFills.blocks_layout?.items || []),
  ...(staticFills || []).map(({ id }) => id),
];

const SlotRenderer = ({ name, metadata }) => {
  const { pathname } = useLocation();

  // a 'properties' like object, with blocks_layout and blocks
  const persistentFillsData = useSelector((state) => {
    return state.slots?.data?.items?.[name] || {};
  });

  const { slots } = Registry;
  if (!slots[name]) {
    return null;
  }

  const slotDefinition = slots[name];
  const {
    computeLayout = defaultComputeLayout,
    RenderChild = RenderSlotFill,
    component: SlotWrapper = DefaultSlotWrapper,
    items: staticFillsData = [],
  } = slotDefinition;

  // assign ids to static fills, if they don't exist
  // this mutatates the config registry!
  staticFillsData.forEach((item) => {
    if (!item.id) item.id = uuid();
  });

  const components = Object.assign(
    {
      ...persistentFillsData.blocks,
    },
    ...staticFillsData.map((info) => ({
      [info.id]: { '@type': 'staticFill', ...info },
    })),
  );

  const layout = semRepeticao(
    computeLayout({
      staticFills: staticFillsData,
      persistentFills: persistentFillsData,
    }),
    components,
    Registry.settings?.mpfSlotsBlocosUnicos || TIPOS_UNICOS_PADRAO,
  );

  return (
    <SlotWrapper name={name}>
      {layout.map((id) => (
        <RenderChild
          key={id}
          id={id}
          slotName={name}
          pathname={pathname}
          properties={components[id]}
          metadata={metadata}
        />
      ))}
    </SlotWrapper>
  );
};

export default SlotRenderer;
